import { Utils } from "../../tree/Utils";
import { WebGLCoreQuadList } from "./WebGLCoreQuadList";
import { WebGLCoreQuadOperation } from "./WebGLCoreQuadOperation";
import { WebGLCoreRenderExecutor } from "./WebGLCoreRenderExecutor";
import { WebGLDefaultShader } from "./shaders/WebGLDefaultShader";
import { WebGLShader } from "./WebGLShader";
import { Renderer } from "../Renderer";
import { WebGLCoreRenderState } from "./WebGLCoreRenderState";
export class WebGLRenderer extends Renderer {
    constructor() {
        super(...arguments);
        this.shaderPrograms = new Map();
    }
    destroy() {
        this.shaderPrograms.forEach((shaderProgram) => shaderProgram.destroy());
    }
    _createDefaultShader(context) {
        return new WebGLDefaultShader(context);
    }
    _getShaderBaseType() {
        return WebGLShader;
    }
    _getShaderAlternative(shaderType) {
        return shaderType.getWebGL();
    }
    createCoreQuadList() {
        return new WebGLCoreQuadList(this.stage.bufferMemory);
    }
    createCoreQuadOperation(context, shader, shaderOwner, renderTextureInfo, scissor, index) {
        return new WebGLCoreQuadOperation(context, shader, shaderOwner, renderTextureInfo, scissor, index);
    }
    createCoreRenderExecutor(context) {
        return new WebGLCoreRenderExecutor(context);
    }
    createCoreRenderState(context) {
        return new WebGLCoreRenderState(context);
    }
    createRenderTexture(w, h, pw, ph) {
        const gl = this.stage.gl;
        const glTexture = gl.createTexture();
        gl.bindTexture(gl.TEXTURE_2D, glTexture);
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, pw, ph, 0, gl.RGBA, gl.UNSIGNED_BYTE, null);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
        glTexture.params = {};
        glTexture.params[gl.TEXTURE_MAG_FILTER] = gl.LINEAR;
        glTexture.params[gl.TEXTURE_MIN_FILTER] = gl.LINEAR;
        glTexture.params[gl.TEXTURE_WRAP_S] = gl.CLAMP_TO_EDGE;
        glTexture.params[gl.TEXTURE_WRAP_T] = gl.CLAMP_TO_EDGE;
        glTexture.options = { format: gl.RGBA, internalFormat: gl.RGBA, type: gl.UNSIGNED_BYTE };
        // We need a specific framebuffer for every render texture.
        glTexture.framebuffer = gl.createFramebuffer();
        glTexture.projection = new Float32Array([2 / w, 2 / h]);
        gl.bindFramebuffer(gl.FRAMEBUFFER, glTexture.framebuffer);
        gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, glTexture, 0);
        return glTexture;
    }
    freeRenderTexture(glTexture) {
        const gl = this.stage.gl;
        gl.deleteFramebuffer(glTexture.framebuffer);
        gl.deleteTexture(glTexture);
    }
    uploadTextureSource(textureSource, options) {
        const gl = this.stage.gl;
        const source = options.source;
        const format = {
            premultiplyAlpha: true,
            hasAlpha: true,
            texParams: {},
            texOptions: {
                format: 0,
                internalFormat: 0,
                type: gl.UNSIGNED_BYTE,
            },
        };
        if (options && options.premultiplyAlpha !== undefined) {
            format.premultiplyAlpha = options.premultiplyAlpha;
        }
        if (options && options.hasAlpha !== undefined) {
            format.hasAlpha = options.hasAlpha;
        }
        if (!format.hasAlpha) {
            format.premultiplyAlpha = false;
        }
        format.texParams = options.texParams || {};
        const glTexture = gl.createTexture();
        gl.bindTexture(gl.TEXTURE_2D, glTexture);
        gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, format.premultiplyAlpha);
        const texParams = format.texParams;
        if (!texParams[gl.TEXTURE_MAG_FILTER])
            texParams[gl.TEXTURE_MAG_FILTER] = gl.LINEAR;
        if (!texParams[gl.TEXTURE_MIN_FILTER])
            texParams[gl.TEXTURE_MIN_FILTER] = gl.LINEAR;
        if (!texParams[gl.TEXTURE_WRAP_S])
            texParams[gl.TEXTURE_WRAP_S] = gl.CLAMP_TO_EDGE;
        if (!texParams[gl.TEXTURE_WRAP_T])
            texParams[gl.TEXTURE_WRAP_T] = gl.CLAMP_TO_EDGE;
        for (const [key, value] of Object.entries(texParams)) {
            gl.texParameteri(gl.TEXTURE_2D, parseInt(key, 10), value);
        }
        const texOptions = format.texOptions;
        texOptions.format = (texOptions && texOptions.format) || (format.hasAlpha ? gl.RGBA : gl.RGB);
        texOptions.type = (texOptions && texOptions.type) || gl.UNSIGNED_BYTE;
        texOptions.internalFormat = (texOptions && texOptions.internalFormat) || texOptions.format;
        this.stage.platform.uploadGlTexture(gl, textureSource, source, texOptions);
        glTexture.params = Utils.cloneObjShallow(texParams);
        glTexture.options = Utils.cloneObjShallow(texOptions);
        return glTexture;
    }
    freeTextureSource(textureSource) {
        this.stage.gl.deleteTexture(textureSource.nativeTexture);
    }
    copyRenderTexture(renderTexture, nativeTexture, options) {
        const gl = this.stage.gl;
        gl.bindTexture(gl.TEXTURE_2D, nativeTexture);
        gl.bindFramebuffer(gl.FRAMEBUFFER, renderTexture.framebuffer);
        const pixelRatio = renderTexture.pixelRatio;
        gl.copyTexSubImage2D(gl.TEXTURE_2D, 0, pixelRatio * (options.sx || 0), pixelRatio * (options.sy || 0), pixelRatio * (options.x || 0), pixelRatio * (options.y || 0), pixelRatio * (options.w || renderTexture.ow), pixelRatio * (options.h || renderTexture.oh));
    }
    getShaderProgram(shaderType) {
        return this.shaderPrograms.get(shaderType);
    }
    setShaderProgram(shaderType, program) {
        this.shaderPrograms.set(shaderType, program);
    }
    onResizeCanvasSize() {
        this.stage.context.renderExecutor.onResizeCanvas();
    }
}
//# sourceMappingURL=WebGLRenderer.js.map